/**
 * Left panel - Module library with search and filter
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Divider,
  ScrollArea,
  Text,
  TextInput,
  Select,
  SimpleGrid,
  Button,
  Group,
  Notification,
  Switch,
  Badge,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { IconSearch, IconCheck, IconX, IconRobot, IconUser, IconShield, IconPrinter } from "@tabler/icons-react";
import { ModuleCard } from "../components/ModuleCard";
import { RegisterRobotModal } from "../components/RegisterRobotModal";
import { PrintBodyModal } from "../components/PrintBodyModal";
import { apiClient } from "../api/client";
import { useIsMobile } from "../hooks";
import type { AdminUser } from "../api/types";
import type { ModuleData } from "../types";
import {
  useModules,
  useModuleSearchTerm,
  useModuleSelectedCategory,
  useModuleSelectedServoSeries,
  useViewerStore,
  useUser,
} from "../store";

export function LeftPanel() {
  const allModules = useModules();
  const user = useUser();
  const navigate = useNavigate();
  // On a phone this panel is the content of a bottom sheet, so it fills the
  // sheet instead of sitting in a fixed-width column.
  const isMobile = useIsMobile();

  const [adminMode, setAdminMode] = useState(false);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);

  // Fetch user list when admin mode is turned on
  useEffect(() => {
    if (adminMode && user?.is_admin) {
      apiClient.getAdminUsers().then((res) => {
        setAdminUsers(res.users);
      }).catch(() => {
        // Silently fail - modules will show user IDs instead of names
      });
    }
  }, [adminMode, user?.is_admin]);

  // Build user_id -> username map
  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of adminUsers) {
      m.set(u.user_id, u.username);
    }
    return m;
  }, [adminUsers]);

  const isAdminMode = user?.is_admin && adminMode;

  // Filter modules: show built-in (no owner) + owned by current user / session (+ all for admin mode)
  const modules = allModules.filter((m) => {
    if (!m.owner_user_ids || m.owner_user_ids.length === 0) return true; // built-in module
    if (isAdminMode) return true; // admin mode sees all
    if (user && m.owner_user_ids.includes(user.user_id)) return true; // owned by current user
    if (window.sessionId && m.owner_user_ids.includes(`session:${window.sessionId}`)) return true; // owned by current session
    return false;
  });
  const searchTerm = useModuleSearchTerm();
  const selectedCategory = useModuleSelectedCategory();
  const selectedServoSeries = useModuleSelectedServoSeries();
  const setSearchTerm = useViewerStore((state) => state.setModuleSearchTerm);
  const setSelectedCategory = useViewerStore((state) => state.setModuleSelectedCategory);
  const setSelectedServoSeries = useViewerStore((state) => state.setModuleSelectedServoSeries);

  const [modalOpened, setModalOpened] = useState(false);
  const [printBodyOpened, setPrintBodyOpened] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    message: string;
    notices?: string[];
  } | null>(null);

  const handleUploadComplete = useCallback(
    (result: { success: boolean; message: string; notices?: string[] }) => {
      setUploadResult(result);
      // Notices describe changes made to the uploaded model, so they stay
      // up until dismissed instead of vanishing after a few seconds.
      if (!result.notices || result.notices.length === 0) {
        setTimeout(() => setUploadResult(null), 5000);
      }
    },
    []
  );

  // Get unique categories
  const categories = ["all", ...Array.from(new Set(modules.map((m) => m.category)))];

  // Get unique servo series (only modules that declare one)
  const servoSeriesOptions = [
    "all",
    ...Array.from(new Set(modules.map((m) => m.servo_series).filter((s): s is string => Boolean(s)))),
  ];

  // Filter modules
  const filteredModules = modules.filter((module) => {
    // A generated variant is one size of a parametric module, not a
    // part in its own right. The library shows the family once; the
    // sizes are reached by placing it and using the inspector, so
    // listing every length ever generated would only be noise. A
    // printed body, branch or link has no family in the library -- it
    // was made to order -- so it is shown.
    if (module.generated_from && modules.some((m) => m.id === module.generated_from)) return false;
    const matchesSearch = module.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || module.category === selectedCategory;
    // A module with no series is a shared/passive part (no servo of its own) -
    // it works with every series, so it stays visible under any filter.
    const matchesServoSeries =
      selectedServoSeries === "all" || !module.servo_series || module.servo_series === selectedServoSeries;
    return matchesSearch && matchesCategory && matchesServoSeries;
  });

  // Split into built-in and user's modules
  const builtInModules = filteredModules.filter(
    (m) => !m.owner_user_ids || m.owner_user_ids.length === 0
  );
  const userModules = filteredModules.filter(
    (m) => m.owner_user_ids && m.owner_user_ids.length > 0
  );

  // Group user modules by owner for admin mode display
  const modulesByOwner = useMemo(() => {
    if (!isAdminMode) return new Map<string, ModuleData[]>();
    const grouped = new Map<string, ModuleData[]>();
    for (const m of userModules) {
      // Use the first non-session owner_user_id as the group key
      const ownerId = m.owner_user_ids?.find((id) => !id.startsWith("session:")) ?? "unknown";
      const existing = grouped.get(ownerId) ?? [];
      existing.push(m);
      grouped.set(ownerId, existing);
    }
    return grouped;
  }, [isAdminMode, userModules]);

  return (
    <Box
      style={{
        width: isMobile ? "100%" : 280,
        height: "100%",
        backgroundColor: "var(--mantine-color-body)",
        borderRight: isMobile ? "none" : "1px solid var(--mantine-color-gray-3)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box p="md" style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
        {/* In the mobile sheet the drawer header already names the panel. */}
        {!isMobile && (
          <Group justify="space-between" mb="sm">
            <Text size="lg" fw={600}>
              Module Library
            </Text>
          </Group>
        )}
        <Button
          size="xs"
          variant="light"
          fullWidth
          mb="xs"
          leftSection={<IconRobot size={14} />}
          onClick={() => setModalOpened(true)}
        >
          Register Robot Model
        </Button>
        <Button
          size="xs"
          variant="light"
          fullWidth
          mb="xs"
          leftSection={<IconPrinter size={14} />}
          onClick={() => setPrintBodyOpened(true)}
        >
          Print a Body or Branch
        </Button>

        {user?.is_admin && (
          <>
            <Group justify="space-between" mb="xs">
              <Switch
                size="xs"
                label={
                  <Group gap={4}>
                    <IconShield size={12} />
                    <Text size="xs">Admin Mode</Text>
                  </Group>
                }
                checked={adminMode}
                onChange={(e) => setAdminMode(e.currentTarget.checked)}
                color="red"
              />
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                leftSection={<IconShield size={12} />}
                // Carry the session along so the console can send the
                // admin back to this assembly instead of a fresh one.
                onClick={() =>
                  navigate(window.sessionId ? `/admin?from=${window.sessionId}` : "/admin")
                }
              >
                Admin Console
              </Button>
            </Group>
          </>
        )}

        {uploadResult && (
          <Notification
            icon={uploadResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
            color={uploadResult.success ? "green" : "red"}
            title={uploadResult.success ? "Success" : "Error"}
            onClose={() => setUploadResult(null)}
            mb="xs"
          >
            {uploadResult.message}
            {uploadResult.notices && uploadResult.notices.length > 0 && (
              <Box mt="xs">
                <Text size="xs" fw={600} mb={4}>
                  Adjustments made to your model
                </Text>
                {uploadResult.notices.map((notice, i) => (
                  <Text key={i} size="xs" c="dimmed" mb={2}>
                    • {notice}
                  </Text>
                ))}
              </Box>
            )}
          </Notification>
        )}

        <TextInput
          placeholder="Search modules..."
          leftSection={<IconSearch size={16} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          size="xs"
          mb="xs"
        />

        <Select
          value={selectedCategory}
          onChange={(value) => setSelectedCategory(value || "all")}
          data={categories.map((cat) => ({
            value: cat,
            label: cat.charAt(0).toUpperCase() + cat.slice(1),
          }))}
          size="xs"
          mb="xs"
        />

        <Select
          label="Servo series"
          description="Only mix modules that share one series - they can't be driven over the same bus otherwise"
          placeholder="All series"
          value={selectedServoSeries}
          onChange={(value) => setSelectedServoSeries(value || "all")}
          data={servoSeriesOptions.map((series) => ({
            value: series,
            label: series === "all" ? "All series" : series.charAt(0).toUpperCase() + series.slice(1),
          }))}
          size="xs"
        />
      </Box>

      <ScrollArea style={{ flex: 1 }} p="sm">
        {modules.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            No modules loaded
          </Text>
        ) : filteredModules.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            No modules match your search
          </Text>
        ) : (
          <>
            {builtInModules.length > 0 && (
              <SimpleGrid cols={2} spacing="xs">
                {builtInModules.map((module) => (
                  <ModuleCard key={module.id} module={module} />
                ))}
              </SimpleGrid>
            )}

            {isAdminMode ? (
              // Admin mode: group modules by owner
              <>
                {Array.from(modulesByOwner.entries()).map(([ownerId, ownerModules]) => {
                  const ownerName = userMap.get(ownerId) ?? ownerId.slice(0, 8);
                  const isOwnModules = ownerId === user?.user_id;
                  return (
                    <div key={ownerId}>
                      <Divider
                        my="sm"
                        label={
                          <Group gap={4}>
                            <IconUser size={12} />
                            <Text size="xs">{ownerName}</Text>
                            {isOwnModules && (
                              <Badge size="xs" variant="light">you</Badge>
                            )}
                            <Badge size="xs" variant="light" color="gray">
                              {ownerModules.length}
                            </Badge>
                          </Group>
                        }
                        labelPosition="center"
                      />
                      <SimpleGrid cols={2} spacing="xs">
                        {ownerModules.map((module) => (
                          <ModuleCard key={module.id} module={module} />
                        ))}
                      </SimpleGrid>
                    </div>
                  );
                })}
              </>
            ) : (
              // Normal mode: show user's own modules
              userModules.length > 0 && (
                <>
                  <Divider
                    my="sm"
                    label={
                      <Group gap={4}>
                        <IconUser size={12} />
                        <Text size="xs">Your Models</Text>
                      </Group>
                    }
                    labelPosition="center"
                  />
                  <SimpleGrid cols={2} spacing="xs">
                    {userModules.map((module) => (
                      <ModuleCard key={module.id} module={module} />
                    ))}
                  </SimpleGrid>
                </>
              )
            )}
          </>
        )}
      </ScrollArea>

      <RegisterRobotModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onUploadComplete={handleUploadComplete}
      />
      <PrintBodyModal
        opened={printBodyOpened}
        onClose={() => setPrintBodyOpened(false)}
        onPrinted={(moduleId) => {
          // Show the new part on its own, so nobody has to hunt for it among seventy cards.
          setSearchTerm(moduleId);
          handleUploadComplete({
            success: true,
            message: `Printed ${moduleId} (shown below). Drag it into the scene; a module dropped next to one of its ports attaches to it.`,
          });
        }}
      />
    </Box>
  );
}
